import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@common/components/datatable';
import Placeholder from '@common/components/placeholder';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import useColumn from './useColumn';
import * as integrationFunctionsSelector from '../../selectors/integration-functions';
import * as integrationCommunitiesSelector from '../../selectors/integration-communities';
import { useSelector } from 'react-redux';
import { ExternalUserItem, ExternalUsersResponse } from './types';
import SearchBar from '@common/components/search-bar';

type TransformedExternalUserItem = Omit<ExternalUserItem, 'externalNetworkIds' | 'externalFunctionIds'> & {
  externalNetworkIds: Array<{ externalId: string; name: string }>;
  externalFunctionIds: Array<{ externalId: string; name: string }>;
};

type Props = {
  isOpen: boolean;
  externalUsers: ExternalUsersResponse | null;
  isLoading?: boolean;
  onClose: () => void;
};

export const ExternalUsersModal = ({
  isOpen,
  externalUsers,
  isLoading = false,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const communitygroups = useSelector(
    integrationCommunitiesSelector.getIntegrationCommunities
  );
  const functiongroups = useSelector(
    integrationFunctionsSelector.getIntegrationFunctions
  );

  const transformedExternalUsers = useMemo(() => {
    if (!externalUsers || !communitygroups || !functiongroups) {
      return externalUsers;
    }

    const usersArray: ExternalUserItem[] = externalUsers.items || [];

    const transformedUsers: TransformedExternalUserItem[] = usersArray.map((user: ExternalUserItem) => ({
      ...user,
      externalNetworkIds:
        user.externalNetworkIds?.map((networkId: string) => {
          const communitygroup = communitygroups.find((group: any) => {
            if (Array.isArray(group.externalId)) {
              return group.externalId.includes(networkId);
            }
            return group.externalId === networkId;
          });
          return {
            externalId: networkId,
            name: communitygroup?.name || `Unlinked - ${networkId}`,
          };
        }) || [],
      externalFunctionIds:
        user.externalFunctionIds?.map((functionId: string) => {
          const functiongroup = functiongroups.find((group: any) => {
            if (Array.isArray(group.externalId)) {
              return group.externalId.includes(functionId);
            }
            return group.externalId === functionId;
          });
          return {
            externalId: functionId,
            name: functiongroup?.name || `Unlinked - ${functionId}`,
          };
        }) || [],
    }));

    return { ...externalUsers, items: transformedUsers };
  }, [externalUsers, communitygroups, functiongroups]);

  const filteredUsers = useMemo(() => {
    if (!transformedExternalUsers?.items) {
      return [];
    }

    if (!searchTerm || !searchTerm.trim()) {
      return transformedExternalUsers.items;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return (transformedExternalUsers.items as TransformedExternalUserItem[]).filter((user: TransformedExternalUserItem) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = (user.email || '').toLowerCase();

      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [transformedExternalUsers, searchTerm]);

  const columns = useColumn();

  const handleSearch = (value: string | null) => {
    if (value === null || value === undefined) {
      setSearchTerm('');
    } else {
      setSearchTerm(value);
    }
  };

  return (
    <ModalWindow show={isOpen} onHide={onClose} size="large">
      <ModalContent
        title={t('organisation:external_users_title')}
        onHide={onClose}
        hideFooter
      >
        <SearchBar
          className="!tw-w-[300px]"
          onSearch={handleSearch}
          placeholder={t('organisation:search_users_placeholder')}
          debounce
          filter={false}
          clearable
        />
        <DataTable
          columns={columns as any}
          data={filteredUsers}
          loading={isLoading}
          stickyHeader
          className="tw-h-[500px]"
          emptyView={
            <Placeholder
              title={t('organisation:external_users_no_data_placeholder')}
              description={t(
                  'organisation:external_users_no_data_description'
                )}
            />
            }
        />
      </ModalContent>
    </ModalWindow>
  );
};
